import { useQuery } from "@tanstack/react-query";
import { z } from "zod";

import { api } from "~/shared/services/api/fetch-client.ts";

// Zod schema for trailer type data validation
const TrailerTypeSchema = z.object({
	code: z.string(),
	displayName: z.string(),
	isRefrigerated: z.boolean(),
});

const TrailerTypesResponseSchema = z.array(TrailerTypeSchema);

export type TrailerType = z.infer<typeof TrailerTypeSchema>;

// Query key factory for trailer types
const trailerTypesQueryKey = "trailer-types";

/**
 * Fetches trailer types from the API
 */
async function fetchTrailerTypes(): Promise<TrailerType[]> {
	const response = await api.get("trailers");
	if (!response.ok) {
		throw new Error(`Failed to fetch trailer types: ${response.statusText}`);
	}
	const data = await response.json();

	// Validate the API response using Zod schema
	return TrailerTypesResponseSchema.parse(data);
}

/**
 * Hook to fetch and cache trailer types data
 *
 * @returns Query result with trailer types data, loading state, and error state
 */
export function useTrailerTypes() {
	const { data, error, isLoading } = useQuery({
		queryKey: [trailerTypesQueryKey],
		queryFn: fetchTrailerTypes,
		staleTime: 5 * 60 * 1000, // 5 minutes - static data stays fresh longer
		gcTime: 10 * 60 * 1000, // 10 minutes - keep in cache longer for static data
		retry: 2,
	});

	return {
		trailerTypes: data,
		isLoading,
		error,
	};
}
